import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  Row,
  Col,
  Button,
  Popconfirm,
  Tag,
  Icon,
  Checkbox,
  List,
  Spin,
  Typography
} from 'antd'
import _ from 'lodash'

import {
  getGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  setGroupPrices,
  getGroupPrices
} from '../../modules/priceGroup'
import { getPrices } from '../../modules/priceManagement'
import { getTierAccessBySlug } from '../../modules/priceGroupTierAccess'
import GroupForm from './components/GroupForm'
import { describeCron } from '../PriceManagement/components/ScheduleForm'

const { Text } = Typography

const GroupTierTags = ({ slug }) => {
  const dispatch = useDispatch()
  const tiers = useSelector(
    (state) => state.priceGroupTierAccess.bySlug[slug] || [],
    []
  )

  useEffect(() => {
    dispatch(getTierAccessBySlug(slug))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  const renderTag = (tier) => (
    <Tag key={tier} color="geekblue">
      {tier}
    </Tag>
  )
  const tagList = tiers.length === 0 ? '-' : tiers.map(renderTag)

  return (
    <Text
      type="secondary"
      style={{
        fontSize: 11,
        display: 'block',
        marginBottom: 6
      }}>
      tier: {tagList}
    </Text>
  )
}

const cardStyle = {
  cursor: 'pointer',
  minHeight: 180,
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}

const sortGroupPriceIds = (groupPrices) => {
  if (!Array.isArray(groupPrices)) return []
  const sorted = [...groupPrices].sort((a, b) => {
    const ao = a && a.sort_order != null ? a.sort_order : 0
    const bo = b && b.sort_order != null ? b.sort_order : 0
    return ao - bo
  })
  return sorted.map((p) => p.id)
}

const PriceGroups = () => {
  const dispatch = useDispatch()
  const [formVisible, setFormVisible] = useState(false)
  const [formData, setFormData] = useState({})
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedPriceIds, setSelectedPriceIds] = useState([])

  const {
    groups,
    isLoading,
    isSaving,
    prices,
    pricesLoading,
    isGroupPricesLoading
  } = useSelector(
    (state) => ({
      groups: state.priceGroup.groups,
      isLoading: state.priceGroup.isLoading,
      isSaving: state.priceGroup.isSaving,
      isGroupPricesLoading: state.priceGroup.isGroupPricesLoading,
      prices: state.priceManagement.prices,
      pricesLoading: state.priceManagement.isLoading
    }),
    []
  )

  useEffect(() => {
    dispatch(getGroups())
  }, [dispatch])

  const onReload = () => {
    dispatch(getGroups())
  }

  const onOpenCreate = () => {
    setFormData({})
    setFormVisible(true)
  }

  const onOpenEdit = (e, group) => {
    e.stopPropagation()
    setFormData(group)
    setFormVisible(true)
  }

  const onCloseForm = () => {
    setFormVisible(false)
    setFormData({})
  }

  const onFormOk = async (data) => {
    const ok = !_.isEmpty(formData)
      ? await dispatch(updateGroup(data))
      : await dispatch(createGroup(data))
    // Always refresh — covers the case where another tab/user changed groups
    // while this form was open, and ensures the list reflects committed state.
    await dispatch(getGroups())
    // Keep the form open on failure (e.g. duplicate slug) so the user can fix
    // and retry without re-entering everything.
    if (!ok) return
    setFormVisible(false)
    setFormData({})
  }

  const onDelete = async (e, id) => {
    e.stopPropagation()
    await dispatch(deleteGroup(id))
    await dispatch(getGroups())
  }

  const onOpenGroup = async (group) => {
    setSelectedGroup(group)
    setSelectedPriceIds([])
    dispatch(getPrices())
    const groupPrices = await dispatch(getGroupPrices(group.id))
    setSelectedPriceIds(sortGroupPriceIds(groupPrices))
  }

  const onBackToGrid = () => {
    setSelectedGroup(null)
    setSelectedPriceIds([])
  }

  const onToggleCheckbox = (priceId, checked) => {
    if (checked) {
      if (!selectedPriceIds.includes(priceId)) {
        setSelectedPriceIds([...selectedPriceIds, priceId])
      }
    } else {
      setSelectedPriceIds(selectedPriceIds.filter((id) => id !== priceId))
    }
  }

  const onMoveUp = (priceId) => {
    const index = selectedPriceIds.indexOf(priceId)
    if (index <= 0) return
    const next = [...selectedPriceIds]
    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
    setSelectedPriceIds(next)
  }

  const onMoveDown = (priceId) => {
    const index = selectedPriceIds.indexOf(priceId)
    if (index < 0 || index >= selectedPriceIds.length - 1) return
    const next = [...selectedPriceIds]
    ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
    setSelectedPriceIds(next)
  }

  const onSavePrices = async () => {
    if (!selectedGroup) return
    await dispatch(setGroupPrices(selectedGroup.id, selectedPriceIds))
    await dispatch(getGroups())
    setSelectedGroup(null)
    setSelectedPriceIds([])
  }

  if (selectedGroup) {
    const selectedSet = new Set(selectedPriceIds)
    const selectedInOrder = selectedPriceIds
      .map((id) => prices.find((p) => p.id === id))
      .filter(Boolean)
    const availablePrices = prices.filter((p) => !selectedSet.has(p.id))

    return (
      <Card
        title={
          <span>
            <Button
              icon="arrow-left"
              size="small"
              style={{ marginRight: 12 }}
              onClick={onBackToGrid}
            />
            จัดการราคาในกลุ่ม: {selectedGroup.name}
          </span>
        }
        extra={
          <Button
            type="primary"
            icon="save"
            loading={isSaving}
            onClick={onSavePrices}>
            บันทึก
          </Button>
        }>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card
              type="inner"
              title={`ราคาที่อยู่ในกลุ่ม (${selectedInOrder.length})`}
              bodyStyle={{ padding: 0 }}>
              <List
                loading={pricesLoading || isGroupPricesLoading}
                dataSource={selectedInOrder}
                locale={{ emptyText: 'ยังไม่มีราคาในกลุ่มนี้' }}
                renderItem={(item, index) => (
                  <List.Item
                    style={{ padding: '8px 16px' }}
                    actions={[
                      <Button
                        size="small"
                        icon="arrow-up"
                        disabled={index === 0}
                        onClick={() => onMoveUp(item.id)}
                      />,
                      <Button
                        size="small"
                        icon="arrow-down"
                        disabled={index === selectedInOrder.length - 1}
                        onClick={() => onMoveDown(item.id)}
                      />,
                      <Button
                        size="small"
                        type="danger"
                        icon="close"
                        onClick={() => onToggleCheckbox(item.id, false)}
                      />
                    ]}>
                    <List.Item.Meta
                      title={
                        <span>
                          <Text strong style={{ marginRight: 8 }}>
                            {index + 1}.
                          </Text>
                          {item.name}
                        </span>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.source_type} · {item.currency}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              type="inner"
              title={`ราคาที่ยังไม่ได้เลือก (${availablePrices.length})`}
              bodyStyle={{ padding: 0 }}>
              <List
                loading={pricesLoading}
                dataSource={availablePrices}
                locale={{ emptyText: 'เลือกราคาครบแล้ว' }}
                renderItem={(item) => (
                  <List.Item style={{ padding: '8px 16px' }}>
                    <Checkbox
                      checked={false}
                      onChange={(e) =>
                        onToggleCheckbox(item.id, e.target.checked)
                      }>
                      {item.name}{' '}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        ({item.source_type} · {item.currency})
                      </Text>
                    </Checkbox>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </Card>
    )
  }

  return (
    <Card
      title="กลุ่มลูกค้า"
      extra={
        <div>
          <Button
            size="small"
            icon="plus"
            type="primary"
            onClick={onOpenCreate}
            style={{ marginRight: 8 }}>
            เพิ่มกลุ่ม
          </Button>
          <Button size="small" icon="reload" onClick={onReload}>
            Reload
          </Button>
        </div>
      }>
      <Row gutter={[16, 16]} type="flex" align="stretch">
        {groups.map((group) => (
          <Col
            xs={24}
            sm={12}
            md={8}
            lg={6}
            key={group.id}
            style={{ display: 'flex' }}>
            <Card
              hoverable
              style={{ ...cardStyle, width: '100%' }}
              bodyStyle={{ flex: 1 }}
              onClick={() => onOpenGroup(group)}
              actions={[
                <Icon
                  type="unordered-list"
                  title="จัดการราคา"
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenGroup(group)
                  }}
                />,
                <Icon
                  type="edit"
                  title="แก้ไข"
                  onClick={(e) => onOpenEdit(e, group)}
                />,
                <Popconfirm
                  title="ยืนยันการลบ?"
                  onConfirm={(e) => {
                    e.stopPropagation()
                    onDelete(e, group.id)
                  }}
                  onCancel={(e) => e.stopPropagation()}>
                  <Icon
                    type="delete"
                    title="ลบ"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              ]}>
              <Card.Meta
                title={group.name}
                description={
                  <div>
                    {group.slug && (
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 11,
                          fontFamily: 'monospace',
                          display: 'block',
                          marginBottom: 6
                        }}>
                        slug:{' '}
                        <Tag style={{ fontFamily: 'monospace' }}>
                          {group.slug}
                        </Tag>
                      </Text>
                    )}
                    {group.slug && <GroupTierTags slug={group.slug} />}
                    <div style={{ marginBottom: 8 }}>
                      {group.discord_webhook_url ? (
                        <Tag color="blue">Discord</Tag>
                      ) : (
                        <Tag>Discord off</Tag>
                      )}
                      {group.line_enabled ? (
                        <Tag color="green">LINE</Tag>
                      ) : (
                        <Tag>LINE off</Tag>
                      )}
                    </div>
                    {group.discord_webhook_url && (
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 12,
                          display: 'block',
                          marginBottom: 4
                        }}>
                        <Icon type="clock-circle" />{' '}
                        {group.discord_cron_expression
                          ? describeCron(group.discord_cron_expression)
                          : 'ยังไม่ตั้งเวลา'}
                      </Text>
                    )}
                    {group.line_enabled && group.line_keyword && (
                      <Text
                        type="secondary"
                        style={{ fontSize: 12, display: 'block' }}>
                        <Icon type="message" /> {group.line_keyword}
                      </Text>
                    )}
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
        {isLoading && groups.length === 0 && (
          <Col span={24} style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin />
          </Col>
        )}
        {!isLoading && groups.length === 0 && (
          <Col span={24}>
            <Text type="secondary">ยังไม่มีกลุ่มลูกค้า</Text>
          </Col>
        )}
      </Row>
      <GroupForm
        visible={formVisible}
        onOk={onFormOk}
        onCancel={onCloseForm}
        formData={formData}
      />
    </Card>
  )
}

export default PriceGroups
